import { create } from 'zustand';

export type Theme = 'light' | 'dark';
export type Language = 'ko' | 'en';

const FONT_SIZES = [12, 13, 14, 16, 18, 20, 22];
const DEFAULT_FONT_LEVEL = 3; // 16px
const FONT_KEY_PREFIX = 'ourhertz_fontLevel_';

interface UIState {
  theme: Theme;
  language: Language;
  currentTab: string;
  fontLevels: Record<string, number>; // per-tab font levels

  setTheme: (theme: Theme) => void;
  setLanguage: (lang: Language) => void;
  setCurrentTab: (tab: string) => void;
  setFontLevel: (level: number) => void;
  increaseFontLevel: () => void;
  decreaseFontLevel: () => void;
  loadFromStorage: () => void;
  getFontLevel: () => number;
}

function loadSetting<T>(key: string, fallback: T): T {
  try {
    const val = localStorage.getItem(key);
    return val ? (JSON.parse(val) as T) : fallback;
  } catch { return fallback; }
}

function applyFontSize(level: number) {
  const px = FONT_SIZES[Math.max(0, Math.min(level, FONT_SIZES.length - 1))];
  document.documentElement.style.fontSize = `${px}px`;
}

function getTabFontLevel(tab: string): number {
  return loadSetting<number>(FONT_KEY_PREFIX + tab, DEFAULT_FONT_LEVEL);
}

export const useUIStore = create<UIState>((set, get) => ({
  theme: 'dark',
  language: 'ko',
  currentTab: '/',
  fontLevels: {},

  setTheme: (theme) => {
    localStorage.setItem('ourhertz_theme', JSON.stringify(theme));
    document.documentElement.classList.toggle('dark', theme === 'dark');
    set({ theme });
  },

  setLanguage: (language) => {
    localStorage.setItem('ourhertz_language', JSON.stringify(language));
    set({ language });
  },

  setCurrentTab: (tab) => {
    const level = getTabFontLevel(tab);
    applyFontSize(level);
    set((s) => ({ currentTab: tab, fontLevels: { ...s.fontLevels, [tab]: level } }));
  },

  setFontLevel: (level) => {
    const clamped = Math.max(0, Math.min(level, FONT_SIZES.length - 1));
    const tab = get().currentTab;
    localStorage.setItem(FONT_KEY_PREFIX + tab, JSON.stringify(clamped));
    applyFontSize(clamped);
    set((s) => ({ fontLevels: { ...s.fontLevels, [tab]: clamped } }));
  },

  increaseFontLevel: () => {
    const tab = get().currentTab;
    const current = get().fontLevels[tab] ?? getTabFontLevel(tab);
    get().setFontLevel(Math.min(current + 1, FONT_SIZES.length - 1));
  },

  decreaseFontLevel: () => {
    const tab = get().currentTab;
    const current = get().fontLevels[tab] ?? getTabFontLevel(tab);
    get().setFontLevel(Math.max(current - 1, 0));
  },

  loadFromStorage: () => {
    const theme = loadSetting<Theme>('ourhertz_theme', 'dark');
    const language = loadSetting<Language>('ourhertz_language', 'ko');
    document.documentElement.classList.toggle('dark', theme === 'dark');
    const level = getTabFontLevel('/');
    applyFontSize(level);
    set({ theme, language, fontLevels: { '/': level } });
  },

  getFontLevel: () => {
    const s = get();
    return s.fontLevels[s.currentTab] ?? getTabFontLevel(s.currentTab);
  },
}));

export { FONT_SIZES };
